import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/20/solid';
import { CpuChipIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import Tag from 'components/common/tag';
import Tooltip from 'components/layout/tooltip';
import EquipmentPlaceholderPhoto from 'components/products/equipment-placeholder-photo';
import ProductLink from 'components/products/product-link';
import ProductPhoto from 'components/products/product-photo';
import {
  AirHandlerProductType,
  SystemOptionCondenserType,
  SystemOptionZoneType
} from 'data/types';
import { ReactNode } from 'react';
import {
  equipmentPlaceholderNote,
  KEPT_EQUIPMENT_PRODUCT_ID
} from 'utils/equipment-placeholder';
import { equipmentPlaceholderKind } from 'utils/equipment-placeholder-kind';
import { abbreviateNumber } from 'utils/helpers';
import { buildSeriesDisplayName } from 'utils/system-products';

const ROW_CLASS =
  'grid grid-cols-[minmax(110px,0.8fr)_minmax(0,1fr)_minmax(0,1.6fr)_auto_auto] items-center gap-3 border-b border-gray-100 px-4 py-2.5 last:border-b-0';

type LockToggleButtonProps = Readonly<{
  locked: boolean;
  subject: string;
  onToggle: () => void;
}>;

export function LockToggleButton({
  locked,
  subject,
  onToggle
}: LockToggleButtonProps) {
  const label = locked ? `Unlock ${subject}` : `Lock ${subject}`;
  return (
    <button
      type='button'
      aria-label={label}
      className='relative text-gray-300 hover:text-gray-400'
      onClick={onToggle}
    >
      {locked ? (
        <LockClosedIcon className='h-5 w-5 text-gray-400' />
      ) : (
        <LockOpenIcon className='h-5 w-5' />
      )}
      <Tooltip text={label} />
    </button>
  );
}

type CondenserHeaderProps = Readonly<{
  condenser: SystemOptionCondenserType;
  zoneCount: number;
  actions?: ReactNode;
}>;

export function CondenserHeader({
  condenser,
  zoneCount,
  actions
}: CondenserHeaderProps) {
  const kept =
    condenser.selectedProduct.productId === KEPT_EQUIPMENT_PRODUCT_ID;
  const seriesName =
    buildSeriesDisplayName(condenser.selectedProduct) ??
    condenser.selectedProduct.series ??
    'Unknown Series';

  return (
    <div className='flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3'>
      <CpuChipIcon className='h-6 w-6 shrink-0 text-gray-800' />
      {kept ? (
        <EquipmentPlaceholderPhoto
          kind={equipmentPlaceholderKind(condenser.selectedProduct, 'outdoor')}
          size='small'
        />
      ) : (
        <ProductPhoto product={condenser.selectedProduct} size='small' />
      )}
      <div className='min-w-0 grow'>
        <div className='flex flex-wrap items-baseline gap-x-2'>
          <span className='font-semibold text-gray-900'>{condenser.name}</span>
          <span className='text-sm text-gray-400'>·</span>
          {kept ? (
            <span className='text-sm font-semibold text-gray-500'>
              {condenser.selectedProduct.name}
            </span>
          ) : (
            <>
              <span className='text-sm font-semibold text-gray-700'>
                {seriesName}
              </span>
              <span className='text-xs'>
                <ProductLink
                  type='condenser'
                  product={condenser.selectedProduct}
                  hoverInfo={false}
                />
              </span>
            </>
          )}
        </div>
        {kept ? (
          <div className='text-xs italic text-gray-400'>
            {equipmentPlaceholderNote(condenser.selectedProduct)}
          </div>
        ) : (
          <div className='text-xs text-gray-500'>
            {condenser.selectedProduct.coolingBTU
              ? `${abbreviateNumber(
                  condenser.selectedProduct.coolingBTU
                )} BTU · `
              : ''}
            Serves {zoneCount} zone{zoneCount === 1 ? '' : 's'}
          </div>
        )}
      </div>
      {actions && (
        <div className='flex shrink-0 items-center gap-1.5'>{actions}</div>
      )}
    </div>
  );
}

type ZoneRowProps = Readonly<{
  zone: SystemOptionZoneType;
  actions?: ReactNode;
}>;

export function ZoneRow({ zone, actions }: ZoneRowProps) {
  const spaceNames =
    zone.type !== 'ducted'
      ? zone.zoneItems.map((item) => item.name).join(', ')
      : '';
  const kept = zone.selectedProduct.productId === KEPT_EQUIPMENT_PRODUCT_ID;

  return (
    <div className={ROW_CLASS}>
      <div className='flex min-w-0 items-center gap-2'>
        <RectangleStackIcon className='h-5 w-5 shrink-0 text-gray-800' />
        <span className='truncate font-semibold text-gray-900'>
          {zone.name}
        </span>
      </div>
      <div className='truncate text-sm text-gray-500' title={spaceNames}>
        {spaceNames}
      </div>
      <div className='flex min-w-0 items-center gap-2'>
        {kept ? (
          <EquipmentPlaceholderPhoto
            kind={equipmentPlaceholderKind(zone.selectedProduct, 'indoor')}
            size='small'
          />
        ) : (
          <ProductPhoto product={zone.selectedProduct} size='small' />
        )}
        <div className='min-w-0'>
          {kept ? (
            <>
              <div className='truncate text-sm font-semibold text-gray-500'>
                {zone.selectedProduct.name}
              </div>
              <div className='truncate text-xs italic text-gray-400'>
                {equipmentPlaceholderNote(zone.selectedProduct)}
              </div>
            </>
          ) : (
            <>
              <div className='truncate text-sm font-semibold text-gray-900'>
                {zone.selectedProduct.coolingBTU
                  ? `${abbreviateNumber(zone.selectedProduct.coolingBTU)} `
                  : ''}
                {zone.selectedProduct.type}
              </div>
              <div className='truncate text-xs'>
                <ProductLink
                  type='air handler'
                  product={zone.selectedProduct}
                  hoverInfo={false}
                />
              </div>
            </>
          )}
        </div>
      </div>
      {!kept && zone.selectedProduct.coolingBTU ? (
        <Tag
          noWrap
          text={`${abbreviateNumber(zone.selectedProduct.coolingBTU)} BTU`}
        />
      ) : (
        <span />
      )}
      <div className='flex items-center gap-1.5'>{actions}</div>
    </div>
  );
}

type BlowerRowProps = Readonly<{
  blower: AirHandlerProductType;
  actions?: ReactNode;
}>;

export function BlowerRow({ blower, actions }: BlowerRowProps) {
  const kept = blower.productId === KEPT_EQUIPMENT_PRODUCT_ID;

  return (
    <div className={ROW_CLASS}>
      <span />
      <span />
      <div className='flex min-w-0 items-center gap-2'>
        {kept ? (
          <EquipmentPlaceholderPhoto
            kind={equipmentPlaceholderKind(blower, 'blower')}
            size='small'
          />
        ) : (
          <ProductPhoto product={blower} size='small' />
        )}
        <div className='min-w-0'>
          {kept ? (
            <>
              <div className='truncate text-sm font-semibold text-gray-500'>
                {blower.name}
              </div>
              <div className='truncate text-xs italic text-gray-400'>
                {equipmentPlaceholderNote(blower)}
              </div>
            </>
          ) : (
            <>
              <div className='truncate text-sm font-semibold text-gray-900'>
                {blower.heatingBTU > 0
                  ? `${abbreviateNumber(blower.heatingBTU)} `
                  : ''}
                {blower.type}
              </div>
              <div className='truncate text-xs'>
                <ProductLink
                  type='air handler'
                  product={blower}
                  hoverInfo={false}
                />
              </div>
            </>
          )}
        </div>
      </div>
      {blower.heatingBTU > 0 ? (
        <Tag noWrap text={`${abbreviateNumber(blower.heatingBTU)} BTU`} />
      ) : (
        <span />
      )}
      {actions ? (
        <div className='flex items-center gap-1.5'>{actions}</div>
      ) : (
        <span />
      )}
    </div>
  );
}
